/**
 * Página post-consulta — Revisión y creación de la receta.
 *
 * Se accede tras hacer clic en "Finalizar consulta" con datos que la Dra. IA
 * sugirió (recibidos vía `location.state.aiRxItems`). El doctor edita las
 * filas, añade/quita medicamentos y decide si crear la receta o saltarse.
 * Luego navega automáticamente a la página de próxima cita.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import { formatErr } from "../../lib/api";
import { ArrowLeft, Pill, Sparkles, Clock } from "@/components/icons";

export default function PostConsultReceta() {
  const { appointmentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const initialItems = location.state?.aiRxItems || [];
  const followUp = location.state?.aiFollowUp || "";
  const patientId = location.state?.patientId;
  const patientName = location.state?.patientName || "Paciente";
  const diagnosis = location.state?.diagnosis || "";

  const [rows, setRows] = useState(
    initialItems.length
      ? initialItems.map((r) => ({
          drug: r.drug || "",
          dose: r.dose || "",
          frequency: r.frequency || "",
          duration: r.duration || "",
          notes: r.notes || "",
        }))
      : [{ drug: "", dose: "", frequency: "", duration: "", notes: "" }],
  );
  const [pharmacies, setPharmacies] = useState([]);
  const [selectedPharmacies, setSelectedPharmacies] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (!showHistory || history !== null || !patientId) return;
    setHistoryLoading(true);
    api
      .get(`/prescriptions?patient_id=${patientId}`)
      .then((r) => setHistory(r.data || []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, [showHistory, history, patientId]);

  useEffect(() => {
    // Si no llegamos con state (refresh o URL directa) y tampoco tenemos
    // patientId, no podemos continuar. Volvemos a la agenda.
    if (!patientId) {
      toast.error("Falta información del paciente. Vuelve a la consulta.");
      navigate("/doctor/agenda", { replace: true });
      return;
    }
    api.get("/users/pharmacies").then(({ data }) => setPharmacies(data || [])).catch(() => {});
    // Pre-seleccionar la farmacia predeterminada del paciente (si tiene).
    api
      .get(`/patients/${patientId}`)
      .then(({ data }) => {
        const pid = data?.patient?.preferred_pharmacy_id;
        if (pid) setSelectedPharmacies([pid]);
      })
      .catch(() => {});
  }, [patientId, navigate]);

  const update = (i, k, v) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const remove = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));
  const addRow = () =>
    setRows((prev) => [
      ...prev,
      { drug: "", dose: "", frequency: "", duration: "", notes: "" },
    ]);

  const canConfirm = rows.some((r) => r.drug.trim());

  const goToNext = () => {
    navigate(`/doctor/consulta/${appointmentId}/certificado`, {
      state: { patientId, patientName, followUp, diagnosis },
      replace: true,
    });
  };

  const skip = () => goToNext();

  const submit = async (e) => {
    e.preventDefault();
    const items = rows
      .filter((r) => r.drug.trim())
      .map((r) => ({
        // Backend `PrescriptionItemIn` expects medication/dosage/frequency/
        // duration/instructions — con duration REQUERIDA. Rellenamos con "—"
        // para no romper la validación cuando el doctor la deja vacía.
        medication: r.drug.trim(),
        dosage: r.dose.trim() || "—",
        frequency: r.frequency.trim() || "—",
        duration: r.duration.trim() || "—",
        instructions: r.notes.trim() || undefined,
      }));
    if (!items.length) {
      toast.error("Añade al menos un medicamento");
      return;
    }
    setSaving(true);
    try {
      await api.post("/prescriptions", {
        patient_id: patientId,
        items,
        pharmacy_ids: selectedPharmacies,
      });
      toast.success("Receta creada");
      goToNext();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const togglePharm = (id) =>
    setSelectedPharmacies((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );

  return (
    <div className="fade-up max-w-3xl mx-auto space-y-4" data-testid="post-receta-page">
      <div className="flex items-center justify-between">
        <Link
          to="/doctor/agenda"
          className="inline-flex items-center gap-2 text-black/60 font-semibold text-[13px] hover:text-black transition"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.75} weight="regular" />
          Volver a la agenda
        </Link>
        <StepIndicator current={1} />
      </div>

      <form
        onSubmit={submit}
        className="bg-[#F5F5F7] rounded-[28px] p-6 sm:p-8 space-y-5"
        data-testid="receta-form"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-[#0071E3] flex items-center justify-center">
              <Pill className="w-5 h-5 text-white" strokeWidth={1.75} weight="regular" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest font-semibold text-black/45">
                Receta médica
              </p>
              <h2 className="text-[22px] font-semibold text-black leading-tight tracking-tight">
                Revisa los medicamentos
              </h2>
              <p className="text-[13px] text-black/55 mt-0.5">
                Paciente: <span className="font-semibold text-black">{patientName}</span>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowHistory(true)}
            data-testid="rx-history-btn"
            className="inline-flex items-center gap-1.5 bg-white hover:bg-black/[0.03] text-black text-[12px] font-semibold rounded-full px-3.5 h-9 transition"
          >
            <Clock className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
            Recetas anteriores
          </button>
        </div>

        <div className="space-y-3">
          {rows.map((r, i) => (
            <div
              key={i}
              data-testid={`rx-item-${i}`}
              className="bg-white rounded-[20px] p-4 space-y-3"
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-widest font-semibold text-black/45">
                  Medicamento {i + 1}
                </span>
                {rows.length > 1 && (
                  <button
                    type="button"
                    onClick={() => remove(i)}
                    className="text-[11px] font-semibold text-red-600 hover:text-red-700 transition"
                  >
                    Quitar
                  </button>
                )}
              </div>
              <label className="block">
                <span className="block text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-1.5">
                  Medicamento
                </span>
                <input
                  value={r.drug}
                  onChange={(e) => update(i, "drug", e.target.value)}
                  placeholder="Ej: Amoxicilina"
                  className="w-full bg-white rounded-xl border border-[#D1D1D6] px-3 h-10 text-[13.5px] font-semibold text-black placeholder:text-black/35 placeholder:font-normal focus:outline-none focus:border-[#0071E3] focus:ring-4 focus:ring-[#0071E3]/15 transition"
                  data-testid={`rx-drug-${i}`}
                />
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                <FieldInput
                  label="Dosis"
                  value={r.dose}
                  onChange={(v) => update(i, "dose", v)}
                  placeholder="500 mg"
                />
                <FieldInput
                  label="Frecuencia"
                  value={r.frequency}
                  onChange={(v) => update(i, "frequency", v)}
                  placeholder="cada 8 horas"
                />
                <FieldInput
                  label="Duración"
                  value={r.duration}
                  onChange={(v) => update(i, "duration", v)}
                  placeholder="5 días"
                />
              </div>
              <FieldInput
                label="Indicaciones adicionales"
                value={r.notes}
                onChange={(v) => update(i, "notes", v)}
                placeholder="Ej: vía oral, con alimentos"
              />
            </div>
          ))}
          <button
            type="button"
            onClick={addRow}
            data-testid="rx-add-row"
            className="w-full inline-flex items-center justify-center gap-2 border border-dashed border-black/15 hover:border-[#0071E3] hover:bg-[#0071E3]/5 text-black/60 hover:text-[#0071E3] rounded-2xl py-3 text-[13px] font-semibold transition"
          >
            + Añadir medicamento
          </button>
        </div>

        {pharmacies.length > 0 && (
          <div className="bg-white rounded-[20px] p-4">
            <p className="text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-2.5">
              Enviar a farmacia (opcional)
            </p>
            <div className="flex flex-wrap gap-2">
              {pharmacies.map((p) => {
                const on = selectedPharmacies.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => togglePharm(p.id)}
                    className={`text-[12px] font-semibold rounded-full px-3.5 h-8 transition ${
                      on
                        ? "bg-[#0071E3] text-white"
                        : "bg-[#F5F5F7] text-black/70 hover:bg-black/[0.06]"
                    }`}
                  >
                    {p.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={skip}
            data-testid="rx-skip"
            className="inline-flex items-center justify-center bg-white hover:bg-black/[0.03] text-black rounded-full px-5 h-11 font-semibold text-[13px] transition"
          >
            Omitir receta y seguir
          </button>
          <button
            type="submit"
            disabled={saving || !canConfirm}
            data-testid="rx-confirm"
            className="inline-flex items-center justify-center gap-2 bg-[#0071E3] text-white hover:bg-[#0077ED] rounded-full px-5 h-11 font-semibold text-[13px] transition disabled:opacity-40"
          >
            {saving ? (
              "Creando receta…"
            ) : (
              <>
                <Sparkles className="w-4 h-4" strokeWidth={1.75} weight="regular" />
                Crear receta y siguiente paso
              </>
            )}
          </button>
        </div>
      </form>

      {showHistory && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setShowHistory(false)}
          data-testid="rx-history-modal"
        >
          <div
            className="bg-white rounded-[28px] border border-black/[0.06] max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-black/50">
                  Historial
                </div>
                <h3 className="text-lg font-extrabold text-black">
                  Recetas anteriores de {patientName}
                </h3>
              </div>
              <button
                onClick={() => setShowHistory(false)}
                data-testid="rx-history-close"
                className="w-8 h-8 rounded-full bg-black/[0.04] hover:bg-black/[0.08] flex items-center justify-center transition"
              >
                <ArrowLeft className="w-3.5 h-3.5 text-black/70 rotate-45" />
              </button>
            </div>
            {historyLoading && (
              <div className="text-sm text-black/60">Cargando…</div>
            )}
            {!historyLoading && history && history.length === 0 && (
              <div className="text-sm text-black/60 py-8 text-center">
                Este paciente aún no tiene recetas anteriores.
              </div>
            )}
            {!historyLoading && history && history.length > 0 && (
              <ul className="space-y-3">
                {history.map((rx) => (
                  <li
                    key={rx.id}
                    className="border border-black/[0.08] rounded-2xl p-4 hover:bg-black/[0.02] transition"
                    data-testid={`rx-history-item-${rx.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-bold text-black/60">
                        {rx.created_at
                          ? new Date(rx.created_at).toLocaleDateString("es-ES", {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            })
                          : "—"}
                      </div>
                      <span className="text-[10px] uppercase tracking-widest font-bold text-[#01BC8E]">
                        {rx.status || "activa"}
                      </span>
                    </div>
                    <ul className="text-sm text-black space-y-1">
                      {(rx.items || []).map((it, idx) => (
                        <li key={idx}>
                          • <span className="font-bold">{it.medication}</span>
                          {it.dosage ? ` — ${it.dosage}` : ""}
                          {it.frequency ? ` · ${it.frequency}` : ""}
                          {it.duration ? ` · ${it.duration}` : ""}
                          {it.instructions ? (
                            <span className="text-black/60"> ({it.instructions})</span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FieldInput({ label, value, onChange, placeholder }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-1.5">
        {label}
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white rounded-xl border border-[#D1D1D6] px-3 h-10 text-[13px] text-black placeholder:text-black/35 focus:outline-none focus:border-[#0071E3] focus:ring-4 focus:ring-[#0071E3]/15 transition"
      />
    </label>
  );
}

/**
 * StepIndicator — Dos pasos "Receta → Próxima cita". `current` es 1 o 2.
 */
export function StepIndicator({ current = 1 }) {
  const Dot = ({ n, active, label }) => (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold transition ${
          active
            ? "bg-black text-white"
            : "bg-white text-black/40"
        }`}
      >
        {n}
      </span>
      <span
        className={`text-[10px] uppercase tracking-widest font-semibold ${
          active ? "text-black" : "text-black/40"
        }`}
      >
        {label}
      </span>
    </span>
  );
  return (
    <div className="flex items-center gap-2.5">
      <Dot n={1} active={current === 1} label="Receta" />
      <span className="text-black/20">·</span>
      <Dot n={2} active={current === 2} label="Próxima cita" />
    </div>
  );
}
